import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQuery";
import {
  CampaignRequest,
  CampaignId,
  FileUploadResponse,
  CampaignDetailsResponse,
  CampaignMembersQuery,
  CampaignLatestClear,
  UrlResponse,
  HubspotUrlRequest,
  ProductAnalysisResponse,
  ProductAnalysisRequest,
  FileUploadNewResponse,
  CampaignPatchMembersResponse,
  CampaignPatchMembersQuery,
  Campaign,
  CurrentCampaign,
  CampaignSummary,
  Response,
  UpdateCampaign,
  EnterCompanyManually,
  ValidateCampaign,
  EngagementScoreConfig,
  ImportFromCRM,
  UpdateKeyTitle,
  CreateKeyTitle,
  UpdateKeyRole,
  Mismatches,
  MismatchAccount,
  ContactsMismatch,
  AccountsMismatch,
  AccountsForScraping,
  TitleGaps,
  CompaniesFilters,
  GenerateTargetAudienceRequest,
  CampaignActivities,
  RoleMembers,
  ListResponse,
  AnalyzedAccount,
  MemberWithoutPlaybook,
  SectionConfig,
  AccountPlanItem,
  MemberPlaybookItem,
  GeoAnalysisResponse,
  GenerateTargetAudienceResponseAPI,
  CIDAddedType,
  IncludeAll,
  MarketoLoginData,
} from "types/apiTypes";
import { NotificationTypes, RequestMethods } from "types/enums";
import {
  AccountPlan,
  ActionType,
  BubbleChartRole,
  ChartAnalytics,
  GeneratePlaybookListItem,
  ListQuery,
  Playbook,
  Query,
  Topic,
} from "../types";
import { prepareQuery } from "../utils/prepareQuery";
import { CampaignMember } from "../types/companyTypes";

export const campaignsApi = createApi({
  reducerPath: "campaignsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Campaign",
    "Campaigns",
    "summary",
    "engagement-config",
    "members",
    "chart",
    "company",
    "accounts-for-scraping",
    "activities",
    "filters",
    "role-members",
    "current-role-members",
    "mismatches",
    "buble-chart",
    "AccountPlan",
    "account-plan-list",
  ] as const,
  endpoints: (builder) => ({
    // POST /api/campaigns
    createCampaign: builder.mutation<CampaignId, CampaignRequest>({
      query: (body) => ({
        url: "/campaigns",
        method: RequestMethods.POST,
        body,
      }),
    }),

    uploadCampaignFiles: builder.mutation<
      FileUploadNewResponse,
      {
        campaign_id: string;
        files: File[];
        import_contacts?: boolean;
        reference_account_id?: string;
        data_source?: string;
      }
    >({
      query: ({
        campaign_id,
        files,
        import_contacts = true,
        reference_account_id,
        data_source,
      }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));

        return {
          url: `/campaigns/${campaign_id}/upload_files${prepareQuery({
            import_contacts,
            reference_account_id,
            data_source,
          })}`,
          method: RequestMethods.POST,
          body: formData,
        };
      },
    }),

    uploadCampaignExtendFile: builder.mutation<
      FileUploadNewResponse,
      { account_id: string; file: File }
    >({
      query: ({ account_id, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/accounts/${account_id}/extend`,
          method: RequestMethods.POST,
          body: formData,
        };
      },
    }),

    createSalesforceUrl: builder.mutation<
      FileUploadResponse,
      HubspotUrlRequest
    >({
      query: (body) => ({
        url: `/integrations/salesforce`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    createHubspotUrl: builder.mutation<FileUploadResponse, HubspotUrlRequest>({
      query: (body) => ({
        url: `/integrations/hubspot`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    // GET /api/campaigns/{campaign_id}
    getCampaignDetails: builder.query<
      CampaignDetailsResponse,
      { account_id: string }
    >({
      query: ({ account_id }) => ({
        url: `/accounts/${account_id}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["company"],
    }),

    getCampaignMembers: builder.query<
      ListResponse<CampaignMember>,
      CampaignMembersQuery
    >({
      query: ({ account_id, ...rest }) => ({
        url: `/accounts/${account_id}/members${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 1,
      providesTags: ["members"],
    }),

    patchCampaignMembers: builder.mutation<
      CampaignPatchMembersResponse,
      CampaignPatchMembersQuery
    >({
      query: ({ account_id, campaign_id, updatedValues }) => ({
        url: `/accounts/${campaign_id}/${account_id}/members`,
        method: RequestMethods.PATCH,
        body: updatedValues,
      }),
      invalidatesTags: ["company", "members", "role-members"],
    }),

    generateCampaign: builder.mutation<
      { task_id: string },
      ProductAnalysisRequest
    >({
      query: ({ url, pdf_files }) => {
        const formData = new FormData();

        pdf_files.forEach((file) => {
          formData.append("files", file);
        });

        if (pdf_files.length === 0) {
          formData.append("files", "");
        }

        return {
          url: "/processors/generate-campaign",
          method: RequestMethods.POST,
          body: formData,
          params: url ? { url } : undefined,
          headers: {
            accept: "application/json",
          },
        };
      },
    }),

    generateCampaignTargetAudience: builder.mutation<
      GenerateTargetAudienceResponseAPI,
      GenerateTargetAudienceRequest
    >({
      query: (body) => ({
        url: `/processors/generate-campaign/target_audience`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getGenerateCampaignResult: builder.query<ProductAnalysisResponse, string>({
      query: (task_id) => ({
        url: `/processors/generate-campaign/${task_id}/result`,
        method: RequestMethods.GET,
      }),
    }),

    getCampaigns: builder.query<Campaign[], Query>({
      query: (params) => ({
        url: `/campaigns${prepareQuery(params)}`,
        method: RequestMethods.GET,
      }),
    }),

    deleteProduct: builder.mutation<CampaignLatestClear, string>({
      query: (campaign_id) => ({
        url: `/campaigns/${campaign_id}`,
        method: RequestMethods.DELETE,
      }),
    }),

    getCurrentCampaign: builder.query<CurrentCampaign, { campaign_id: string }>(
      {
        query: ({ campaign_id }) => ({
          url: `/campaigns/${campaign_id}`,
          method: RequestMethods.GET,
        }),
        providesTags: ["Campaign"],
      },
    ),

    getCampaignSummary: builder.query<CampaignSummary, { campaign_id: string }>(
      {
        query: ({ campaign_id }) => ({
          url: `/campaigns/${campaign_id}/summary`,
          method: RequestMethods.GET,
        }),
        providesTags: ["summary"],
      },
    ),

    patchCampaign: builder.mutation<Response, UpdateCampaign>({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}`,
        method: RequestMethods.PATCH,
        body,
      }),
    }),
    enterCompanyManually: builder.mutation<Response, EnterCompanyManually>({
      query: ({ campaign_id, companies }) => ({
        url: `/campaigns/${campaign_id}/enter-manually`,
        method: RequestMethods.POST,
        body: companies,
      }),
    }),
    validateCampaignData: builder.mutation<Response, ValidateCampaign>({
      query: (body) => ({
        url: "/validators/campaign",
        method: RequestMethods.POST,
        body,
      }),
    }),

    getEngagementScoreConfig: builder.query<EngagementScoreConfig, CampaignId>({
      query: ({ campaign_id }) => ({
        url: `/campaigns/${campaign_id}/engagement-config`,
        method: RequestMethods.GET,
      }),
      providesTags: ["engagement-config"],
    }),

    changeEngagementScoreConfig: builder.mutation<
      EngagementScoreConfig,
      EngagementScoreConfig & CampaignId
    >({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/engagement-config`,
        method: RequestMethods.POST,
        body: rest,
      }),
    }),

    updateExternalData: builder.mutation<Response, ListQuery>({
      query: ({ account_id, campaign_id }) => ({
        url: `/accounts/${campaign_id}/${account_id}/update-with-external-data`,
        method: RequestMethods.POST,
      }),
      invalidatesTags: ["activities"],
    }),

    createImportFromCRM: builder.mutation<
      FileUploadResponse,
      ImportFromCRM & CampaignId
    >({
      query: ({ campaign_id, ...body }) => {
        return {
          url: `/campaigns/${campaign_id}/import-from-crm`,
          method: RequestMethods.POST,
          body,
        };
      },
    }),

    getCRMUrl: builder.mutation<
      UrlResponse,
      {
        campaign_id: string;
        action_type: ActionType;
        redirect_path: string;
        crm: string;
      }
    >({
      query: ({ crm, ...params }) => ({
        url: `/integrations/${crm}`,
        method: RequestMethods.GET,
        params,
      }),
    }),

    updateKeyTitle: builder.mutation<Response, UpdateKeyTitle>({
      query: ({ id, campaign_id, ...body }) => ({
        url: `/key-titles/${campaign_id}/${id}`,
        method: RequestMethods.PATCH,
        body,
      }),
      invalidatesTags: ["Campaign"],
    }),

    createKeyTitle: builder.mutation<Response, CreateKeyTitle>({
      query: ({ campaign_id, ...body }) => ({
        url: `/key-titles/${campaign_id}`,
        method: RequestMethods.POST,
        body,
      }),
      invalidatesTags: ["Campaign"],
    }),

    deleteKeyTitle: builder.mutation<
      Response,
      { campaign_id: string; key_id: string }
    >({
      query: ({ campaign_id, key_id }) => ({
        url: `/key-titles/${campaign_id}/${key_id}`,
        method: RequestMethods.DELETE,
      }),
      invalidatesTags: ["Campaign"],
    }),

    updateKeyRole: builder.mutation<Response, UpdateKeyRole>({
      query: ({ id, campaign_id, ...body }) => ({
        url: `/key-roles/${campaign_id}/${id}`,
        method: RequestMethods.PATCH,
        body,
      }),
      invalidatesTags: ["Campaign"],
    }),

    regenerateSuggestedTitles: builder.mutation<Response, string>({
      query: (campaign_id) => ({
        url: `/key-titles/${campaign_id}/regenerate_suggested_titles`,
        method: RequestMethods.POST,
      }),
      invalidatesTags: ["Campaign"],
    }),

    deleteAllTitles: builder.mutation<Response, string>({
      query: (campaign_id) => ({
        url: `/key-titles/${campaign_id}/delete_titles`,
        method: RequestMethods.DELETE,
      }),
      invalidatesTags: ["Campaign"],
    }),

    getMismatches: builder.query<
      Mismatches<AccountsMismatch<MismatchAccount>, ContactsMismatch>,
      string
    >({
      query: (campaign_id) => ({
        url: `/campaigns/${campaign_id}/mismatches`,
        method: RequestMethods.GET,
      }),
      providesTags: ["mismatches"],
    }),

    resolveMismatches: builder.mutation<
      Response,
      Mismatches<AccountsMismatch<string>, string> & { campaign_id: string }
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/mismatches`,
        method: RequestMethods.POST,
        body,
      }),
      invalidatesTags: ["summary"],
    }),

    getCompaniesFilters: builder.query<CompaniesFilters, void>({
      query: () => ({
        url: "/companies",
        method: RequestMethods.GET,
      }),
      providesTags: ["filters"],
    }),

    getAccountsForScraping: builder.query<AccountsForScraping, string>({
      query: (campaign_id) => ({
        url: `/campaigns/${campaign_id}/accounts-for-scraping`,
        method: RequestMethods.GET,
      }),
      providesTags: ["accounts-for-scraping"],
    }),

    getTitlesForAccounts: builder.mutation<
      TitleGaps,
      { account_ids: string[]; campaign_id: string }
    >({
      query: ({ campaign_id, account_ids }) => ({
        url: `/campaigns/${campaign_id}/title-gaps`,
        method: RequestMethods.POST,
        body: { account_ids },
      }),
    }),

    findExternalDataForAccounts: builder.mutation<
      Response,
      { account_ids: string[]; campaign_id: string } & TitleGaps
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/find-external-data`,
        method: RequestMethods.POST,
        body,
      }),
      invalidatesTags: ["summary", "activities"],
    }),

    getCompaniesActivities: builder.query<CampaignActivities[], string>({
      query: (campaign_id) => ({
        url: `/campaigns/${campaign_id}/activities`,
        method: RequestMethods.GET,
      }),
      providesTags: ["activities"],
    }),

    deleteCompaniesActivities: builder.mutation<
      Response,
      { type: NotificationTypes; account_ids: string[] } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/activities`,
        method: RequestMethods.DELETE,
        body,
      }),
      invalidatesTags: ["activities"],
    }),

    findExternalDataRetry: builder.mutation<
      Response,
      { account_ids: string[]; campaign_id: string }
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/retry-scraping`,
        method: RequestMethods.POST,
        body,
      }),
      invalidatesTags: ["summary", "activities"],
    }),

    restoreDemoCampaign: builder.mutation<{ id: string }, string>({
      query: (campaign_id) => ({
        url: `/campaigns/${campaign_id}/reset-demo-campaign`,
        method: RequestMethods.POST,
      }),
    }),

    getCampaignRoleMembers: builder.query<
      { roles: RoleMembers[] },
      { account_id: string } & Query
    >({
      query: ({ account_id, ...rest }) => ({
        url: `/accounts/${account_id}/role-view${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["role-members"],
    }),

    getCampaignCurrentRoleMembers: builder.query<
      RoleMembers,
      { account_id: string } & Query
    >({
      query: ({ account_id, role_id, ...rest }) => ({
        url: `/accounts/${account_id}/role-view/${role_id}${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["current-role-members"],
      keepUnusedDataFor: 1,
    }),

    getAccountPlan: builder.query<AccountPlan, string>({
      query: (account_id) => ({
        url: `/account-plans/${account_id}`,
        method: RequestMethods.GET,
      }),
    }),

    startAccountPlanGeneration: builder.mutation<
      { success: boolean; job_id?: string; status?: string; error?: string },
      Query
    >({
      query: ({ account_id, campaign_id }) => ({
        url: `/account-plans/${campaign_id}/${account_id}/start-abm-job`,
        method: RequestMethods.POST,
      }),
    }),

    getPDFPlan: builder.mutation<Blob, ListQuery & { topics: Topic[] }>({
      query: ({ account_id, campaign_id, ...body }) => ({
        url: `/account-plans/${campaign_id}/${account_id}/generate-account-plan-pdf`,
        method: RequestMethods.POST,
        responseHandler: (response) => response.blob(),
        headers: {
          Accept: "application/pdf",
          "Content-Type": "application/json",
        },
        body,
      }),
    }),

    updateNotes: builder.mutation<
      Response,
      { campaign_id: string; notes: string }
    >({
      query: ({ campaign_id, notes }) => ({
        url: `/campaigns/${campaign_id}/notes`,
        method: RequestMethods.PATCH,
        body: { notes },
      }),
      invalidatesTags: ["Campaign"],
    }),

    disconnectCRM: builder.mutation<Response, "hubspot" | "salesforce">({
      query: (crm) => ({
        url: `/integrations/${crm}`,
        method: RequestMethods.DELETE,
      }),
      invalidatesTags: ["summary"],
    }),

    getBubbleChart: builder.query<
      { roles: BubbleChartRole[]; analytics: ChartAnalytics },
      { id: string; isAccounts?: boolean } & Query
    >({
      query: ({ id, isAccounts, ...rest }) => ({
        url: `/${isAccounts ? "campaigns" : "accounts"}/${id}/bubble-chart${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["buble-chart"],
    }),

    getAnalyzedAccounts: builder.query<AnalyzedAccount[], ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/analyzed-accounts${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
    }),

    generateAccountsPlanList: builder.mutation<Response, Query>({
      query: ({ campaign_id, ...body }) => ({
        url: `/account-plans/${campaign_id}/start-bulk-abm-job`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    updateAccountPlanGeneration: builder.mutation<
      { success: boolean; job_id?: string; status?: string; error?: string },
      Query
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/account-plans/${campaign_id}/start-updating-account-plans`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    startPlaybookGeneration: builder.mutation<Response, Query>({
      query: ({ member_id, campaign_id }) => ({
        url: `/sdr-playbook/${campaign_id}/${member_id}/start-sdr-playbook-generation`,
        method: RequestMethods.POST,
      }),
    }),

    getPlaybook: builder.query<Playbook, Query>({
      query: ({ member_id }) => ({
        url: `/sdr-playbook/${member_id}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    enterCID: builder.mutation<
      Response,
      CampaignId & {
        cids: (Omit<CIDAddedType, "isAdded" | "sub_cids"> & {
          sub_cids: string[];
        })[];
      }
    >({
      query: ({ campaign_id, cids }) => {
        const formData = new FormData();

        formData.append(
          "cids",
          JSON.stringify(
            cids.map(({ cid, sub_cids, urls }) => ({
              cid,
              sub_cids: sub_cids.length === 0 ? undefined : sub_cids,
              hg_file_urls: urls.length === 0 ? undefined : urls,
            })),
          ),
        );

        cids.forEach(({ files, cid }) => {
          (files || []).forEach((file) => {
            formData.append(cid, file);
          });
        });

        return {
          url: `/campaigns/${campaign_id}/enter-cid`,
          method: RequestMethods.POST,
          body: formData,
          headers: {
            accept: "application/json",
          },
        };
      },
    }),

    getMembersWithoutPlaybook: builder.query<
      ListResponse<MemberWithoutPlaybook>,
      Query
    >({
      query: ({ account_id, ...rest }) => ({
        url: `/sdr-playbook/${account_id}/members-without-playbook${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    generatePlaybookForMembers: builder.mutation<
      Response,
      CampaignId & { ids: string[]; include_all: boolean }
    >({
      query: ({ campaign_id, ids, include_all }) => ({
        url: `/sdr-playbook/${campaign_id}/start-sdr-playbooks-generation?include_all=${include_all}`,
        method: RequestMethods.POST,
        body: ids,
      }),
    }),

    getAccountsPlanList: builder.query<
      ListResponse<AccountPlanItem>,
      ListQuery
    >({
      query: ({ campaign_id, ...rest }) => ({
        url: `/account-plans/${campaign_id}/reports${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["account-plan-list"],
      keepUnusedDataFor: 0,
    }),

    editCompanyData: builder.mutation<
      Response,
      CampaignId & { name: string; website_url?: string; account_id: string }
    >({
      query: ({ campaign_id, account_id, ...body }) => ({
        url: `/accounts/${campaign_id}/${account_id}`,
        method: RequestMethods.PATCH,
        body,
      }),
    }),

    removeMembers: builder.mutation<
      Response,
      CampaignId & { member_ids: string[]; account_id: string }
    >({
      query: ({ campaign_id, account_id, ...body }) => ({
        url: `/accounts/${campaign_id}/${account_id}/members`,
        method: RequestMethods.DELETE,
        body,
      }),
    }),

    getSectionConfig: builder.query<SectionConfig, CampaignId>({
      query: ({ campaign_id }) => ({
        url: `/campaigns/${campaign_id}/fields-config`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    setSectionConfig: builder.mutation<Response, CampaignId & SectionConfig>({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/fields-config`,
        method: RequestMethods.PATCH,
        body,
      }),
    }),

    generatePlaybookPDF: builder.mutation<
      Blob,
      { topics: Playbook; member_id: string } & CampaignId
    >({
      query: ({ member_id, campaign_id, ...body }) => ({
        url: `/sdr-playbook/${campaign_id}/generate-playbook-pdf/${member_id}`,
        method: RequestMethods.POST,
        responseHandler: (response) => response.blob(),
        headers: {
          Accept: "application/pdf",
          "Content-Type": "application/json",
        },
        body,
      }),
    }),

    exportAccountPlans: builder.mutation<UrlResponse, ListQuery>({
      query: ({ campaign_id, ...body }) => ({
        url: `/account-plans/${campaign_id}/export-account-plans`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getPlaybooksCSV: builder.query<UrlResponse, string>({
      query: (account_id) => ({
        url: `/sdr-playbook/${account_id}/export-csv`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    generatePlaybooksCSVForCampaign: builder.mutation<
      UrlResponse,
      {
        account_ids: string[];
        include_all: boolean;
        file_name: string;
      } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/sdr-playbook/export-csv-for-campaign/${campaign_id}`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getAccountsForPlaybooks: builder.query<
      ListResponse<GeneratePlaybookListItem> & {
        total_members: number;
        all_have_hg_data: boolean;
      },
      ListQuery
    >({
      query: ({ campaign_id, ...rest }) => ({
        url: `/sdr-playbook/campaign/${campaign_id}/accounts${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    generatePlaybooksForAccounts: builder.mutation<Response, ListQuery>({
      query: ({ campaign_id, account_ids, include_all }) => ({
        url: `/sdr-playbook/${campaign_id}/start-accounts-playbooks-generation`,
        method: RequestMethods.POST,
        body: { account_ids, include_all },
      }),
      invalidatesTags: ["summary", "company", "activities"],
    }),

    removeAccountPlan: builder.mutation<Response, { id: string } & CampaignId>({
      query: ({ campaign_id, id }) => ({
        url: `/account-plans/${campaign_id}/${id}/account-plan`,
        method: RequestMethods.DELETE,
      }),
    }),

    getAccountMembers: builder.query<ListResponse<MemberPlaybookItem>, Query>({
      query: ({ account_id, ...rest }) => ({
        url: `/sdr-playbook/account/${account_id}/member-playbooks${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    generateAccountPlansCSVForCampaign: builder.mutation<
      UrlResponse,
      {
        account_ids: string[];
        include_all: boolean;
        file_name: string;
      } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/account-plans/${campaign_id}/export-csv`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    analyzeGeoTargeting: builder.mutation<
      GeoAnalysisResponse,
      { text: string }
    >({
      query: (body) => ({
        url: "/campaigns/analyze-geo-targeting",
        method: RequestMethods.POST,
        body,
      }),
    }),

    analyzeJobTitles: builder.mutation<
      GenerateTargetAudienceResponseAPI,
      { text: string }
    >({
      query: (body) => ({
        url: "/campaigns/analyze-job-titles",
        method: RequestMethods.POST,
        body,
      }),
    }),

    addJobTitles: builder.mutation<Response, { text: string } & CampaignId>({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/add-job-titles`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    checkForDataSource: builder.mutation<
      { has_data_source_column: boolean },
      File[]
    >({
      query: (files) => {
        const body = new FormData();
        files.forEach((file) => body.append("file", file));

        return {
          url: "/campaigns/check-data-source-column",
          method: RequestMethods.POST,
          body,
        };
      },
    }),

    removeAccountsPlaybooks: builder.mutation<
      Response,
      { account_ids: string[] } & CampaignId & IncludeAll
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/sdr-playbook/${campaign_id}`,
        method: RequestMethods.DELETE,
        body,
      }),
      invalidatesTags: ["company", "summary", "activities"],
    }),

    removeAccountPlaybooks: builder.mutation<
      Response,
      {
        account_id: string;
        members_ids: string[];
      } & CampaignId &
        IncludeAll
    >({
      query: ({ campaign_id, account_id, ...body }) => ({
        url: `/sdr-playbook/${campaign_id}/${account_id}`,
        method: RequestMethods.DELETE,
        body,
      }),
    }),

    createMarketoIntegration: builder.mutation<Response, MarketoLoginData>({
      query: (body) => ({
        url: "/integrations/marketo",
        method: RequestMethods.POST,
        body,
      }),
    }),

    removeMarketoIntegration: builder.mutation<Response, void>({
      query: () => ({
        url: "/integrations/marketo",
        method: RequestMethods.DELETE,
      }),
    }),

    importDataFromCRM: builder.mutation<Response, ImportFromCRM & CampaignId>({
      query: ({ campaign_id, ...body }) => ({
        url: `/campaigns/${campaign_id}/import-from-crm`,
        method: RequestMethods.POST,
        body,
      }),
    }),
  }),
});

export const {
  useCreateCampaignMutation,
  useCreateHubspotUrlMutation,
  useCreateSalesforceUrlMutation,
  useGetCampaignDetailsQuery,
  useGetCampaignMembersQuery,
  usePatchCampaignMembersMutation,
  useGenerateCampaignMutation,
  useGetGenerateCampaignResultQuery,
  useGetCampaignsQuery,
  useDeleteProductMutation,
  useGetCurrentCampaignQuery,
  useGetCampaignSummaryQuery,
  useLazyGetCampaignSummaryQuery,
  usePatchCampaignMutation,
  useEnterCompanyManuallyMutation,
  useValidateCampaignDataMutation,
  useGetEngagementScoreConfigQuery,
  useChangeEngagementScoreConfigMutation,
  useUpdateExternalDataMutation,
  useCreateImportFromCRMMutation,
  useGetCRMUrlMutation,
  useUpdateKeyTitleMutation,
  useCreateKeyTitleMutation,
  useDeleteKeyTitleMutation,
  useUpdateKeyRoleMutation,
  useRegenerateSuggestedTitlesMutation,
  useLazyGetMismatchesQuery,
  useResolveMismatchesMutation,
  useLazyGetAccountsForScrapingQuery,
  useGetTitlesForAccountsMutation,
  useFindExternalDataForAccountsMutation,
  useGetCompaniesFiltersQuery,
  useGenerateCampaignTargetAudienceMutation,
  useUploadCampaignFilesMutation,
  useGetCompaniesActivitiesQuery,
  useDeleteCompaniesActivitiesMutation,
  useFindExternalDataRetryMutation,
  useRestoreDemoCampaignMutation,
  useGetCampaignRoleMembersQuery,
  useGetCampaignCurrentRoleMembersQuery,
  useGetAccountPlanQuery,
  useUpdateNotesMutation,
  useGetPDFPlanMutation,
  useDisconnectCRMMutation,
  useLazyGetBubbleChartQuery,
  useStartAccountPlanGenerationMutation,
  useGenerateAccountsPlanListMutation,
  useUpdateAccountPlanGenerationMutation,
  useGetPlaybookQuery,
  useEnterCIDMutation,
  useGetMembersWithoutPlaybookQuery,
  useGeneratePlaybookForMembersMutation,
  useGetAccountsPlanListQuery,
  useEditCompanyDataMutation,
  useRemoveMembersMutation,
  useGetSectionConfigQuery,
  useSetSectionConfigMutation,
  useGeneratePlaybookPDFMutation,
  useExportAccountPlansMutation,
  useLazyGetPlaybooksCSVQuery,
  useLazyGetPlaybookQuery,
  useGeneratePlaybooksCSVForCampaignMutation,
  useGetAccountsForPlaybooksQuery,
  useGeneratePlaybooksForAccountsMutation,
  useRemoveAccountPlanMutation,
  useGetAccountMembersQuery,
  useGenerateAccountPlansCSVForCampaignMutation,
  useAnalyzeGeoTargetingMutation,
  useAnalyzeJobTitlesMutation,
  useAddJobTitlesMutation,
  useCheckForDataSourceMutation,
  useRemoveAccountsPlaybooksMutation,
  useRemoveAccountPlaybooksMutation,
  useCreateMarketoIntegrationMutation,
  useRemoveMarketoIntegrationMutation,
  useImportDataFromCRMMutation,
} = campaignsApi;
